import React, { useMemo } from 'react';
import { useMarketData } from '../context/MarketContext';
import { useTheme } from '../context/ThemeContext.js';

const SupplyDemandGraph = () => {
  const { marketData, marketHistory } = useMarketData();
  const { isLight } = useTheme();
  const accentHex = isLight ? '#5e19e6' : '#00f0ff';
  const gridColor = isLight ? 'rgba(26,26,46,0.1)' : 'white';

  const currentPrice = marketData?.price ?? 4291.05;
  const priceChange = marketData?.change ?? 0;

  const visuals = useMemo(() => {
    if (marketHistory.length === 0) return { currentY: 200 };

    const prices = marketHistory.map(h => h.price);
    const padding = 1.002;
    const minP = Math.min(...prices) / padding;
    const maxP = Math.max(...prices) * padding;
    const pRange = maxP - minP || 1;

    const getY = (p) => 350 - ((p - minP) / pRange) * 300;
    const currentY = getY(currentPrice);

    const xStep = 300 / Math.max(marketHistory.length - 1, 1);
    const pricePath = `M ${marketHistory.map((h, i) => `${i * xStep},${getY(h.price)}`).join(' L ')}`;

    const curveIntensity = 80;
    const reaction = priceChange * 5;

    const supplyStep = `M 50,${currentY + curveIntensity + reaction} L 300,${currentY - curveIntensity + reaction}`;
    const demandStep = `M 50,${currentY - curveIntensity - reaction} L 300,${currentY + curveIntensity - reaction}`;

    // --- SYNCED BOUNDS ---
    const volatility = Math.max(15, Math.abs(priceChange) * 40);
    const syncHeight = volatility * 1.8; // Shared vertical span
    const bellBaseX = 180; // Shared vertical spine where both shapes meet

    // Expectation Cone (Shaded region leading into the curve)
    const expectationPath = `M 0,${currentY}
      C 80,${currentY - syncHeight} 150,${currentY - syncHeight} ${bellBaseX},${currentY - syncHeight}
      L ${bellBaseX},${currentY + syncHeight}
      C 150,${currentY + syncHeight} 80,${currentY + syncHeight} 0,${currentY} Z`;

    // Normal Distribution (Solid curve overlapping the cone)
    const stdDev = volatility * 1.2;
    const bellPoints = [];
    for (let y = currentY - syncHeight; y <= currentY + syncHeight; y += 2) {
      const exponent = -Math.pow(y - currentY, 2) / (2 * Math.pow(stdDev, 2));
      const probability = Math.exp(exponent);
      const x = bellBaseX - (probability * 150);
      bellPoints.push(`${x},${y}`);
    }
    // Closing path at the spine for a clean fill
    const normalDistPath = `M ${bellBaseX},${currentY - syncHeight} L ${bellPoints.join(' L ')} L ${bellBaseX},${currentY + syncHeight} Z`;

    return { pricePath, supplyStep, demandStep, expectationPath, normalDistPath, currentY, bellBaseX };
  }, [marketHistory, currentPrice, priceChange]);

  if (!marketData || marketHistory.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <div className={`w-4 h-4 border-2 border-t-transparent rounded-full animate-spin ${isLight ? 'border-primary' : 'border-accent'}`} />
          <div className={`font-mono text-[10px] tracking-widest uppercase ${isLight ? 'text-primary/40' : 'text-accent/40'}`}>Syncing...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative group p-4 overflow-hidden">
      <svg
        className="w-full h-full opacity-90 transition-opacity duration-700 group-hover:opacity-100"
        viewBox="0 0 1000 400"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id="sd-glow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <linearGradient id="expectation-gradient" x1="1" y1="0" x2="0" y2="0">
            <stop offset="0%" stopColor={accentHex} stopOpacity="0.4" />
            <stop offset="100%" stopColor={accentHex} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Prominent Horizontal Line */}
        <line x1="0" y1={visuals.currentY} x2="1000" y2={visuals.currentY} stroke={accentHex} strokeWidth="1.5" strokeDasharray="6 4" opacity="0.6" />

        <line x1="333" y1="50" x2="333" y2="350" stroke={gridColor} strokeWidth="1" opacity="0.05" />
        <line x1="666" y1="50" x2="666" y2="350" stroke={gridColor} strokeWidth="1" opacity="0.05" />

        {/* PANEL 1 */}
        <g transform="translate(20, 0)">
          <path d={visuals.pricePath} stroke={accentHex} strokeWidth="2" fill="none" filter="url(#sd-glow)" style={{ transition: 'all 0.3s ease-out' }} />
        </g>

        {/* PANEL 2 */}
        <g transform="translate(333, 0)">
          <path d={visuals.supplyStep} stroke="#ff4d4d" strokeWidth="2.5" fill="none" opacity="0.8" style={{ transition: 'all 0.3s ease-out' }} />
          <path d={visuals.demandStep} stroke="#5e19e6" strokeWidth="2.5" fill="none" opacity="0.8" style={{ transition: 'all 0.3s ease-out' }} />
        </g>

        {/* PANEL 3: OVERLAPPING PROBABILITY FIELD */}
        <g transform="translate(680, 0)">
          {/* Shaded Tail */}
          <path d={visuals.expectationPath} fill="url(#expectation-gradient)" style={{ transition: 'all 0.3s ease-out' }} />

          {/* Solid Overlapping Bell Curve */}
          <path
                d={visuals.normalDistPath}
                fill={accentHex}
                fillOpacity="0.2"
                stroke="none"
                style={{ transition: 'all 0.3s ease-out' }}
            />

          <g transform={`translate(0, ${visuals.currentY})`} style={{ transition: 'all 0.3s ease-out' }}>
            <rect x="210" y="-15" width="100" height="30" fill={accentHex} rx="2" filter="url(#sd-glow)" />
            <text x="260" y="4" fill={isLight ? 'white' : 'black'} fontSize="12" fontWeight="bold" fontFamily="monospace" textAnchor="middle">
              ${currentPrice.toFixed(2)}
            </text>
            <text x="260" y="28" fill={priceChange >= 0 ? "#00ff88" : "#ff4d4d"} fontSize="9" fontFamily="monospace" textAnchor="middle" fontWeight="bold">
              {priceChange >= 0 ? '▲' : '▼'} {Math.abs(priceChange).toFixed(2)}%
            </text>
            <path d="M 210,0 L 200,-5 L 200,5 Z" fill={accentHex} />
          </g>
        </g>
      </svg>
    </div>
  );
};

export default SupplyDemandGraph;
